import type { AssistantTurnRow, QuestionGroup } from "./types";

/**
 * Collapses the spellings of one question onto a single key.
 *
 * Case, punctuation and spacing are the differences merchants introduce
 * without meaning a different question, so they are removed: "How do I close
 * my account?" and "how do i close my account" are one gap. Curly and straight
 * apostrophes unify, and apostrophes are dropped entirely so "don't" and
 * "dont" meet.
 *
 * Nothing beyond that. Wording this does not merge — "close my account" versus
 * "delete my profile" — stays separate and is read as two rows; clustering
 * them is a different problem and guessing at it here would silently merge
 * questions that are not the same.
 *
 * Returns "" for a question with no letters or digits at all, which the caller
 * treats as carrying no signal.
 */
export function normalizeQuestion(question: string): string {
  return (
    question
      // NFC first. A combining mark is not a letter or a number, so without this
      // the decomposed spelling of "café" has its accent collapsed to a space
      // and splits from the precomposed one — two groups for one question, and
      // the ranking is by how many merchants asked.
      .normalize("NFC")
      .toLowerCase()
      .replace(/[‘’ʼ']/gu, "")
      .replace(/[^\p{L}\p{N}]+/gu, " ")
      .trim()
  );
}

/**
 * The reasons one turn failed for, split out of the stored string.
 *
 * `ask-assistant.ts` joins every attempt's failure with "; ", so a turn that
 * failed twice carries both. Splitting rather than showing the raw string
 * keeps two turns that failed the same way from reading as two problems.
 */
function reasonsOf(row: AssistantTurnRow): string[] {
  const raw = row.groundFailure;
  if (raw === null || raw === undefined || raw.trim() === "") return [];
  return raw
    .split(";")
    .map((part) => part.trim())
    .filter((part) => part !== "");
}

/**
 * Groups turns by normalised question and ranks them most-asked first.
 *
 * Ranked by distinct merchants rather than by row count: one merchant asking
 * the same thing five times is one gap, and ranking by rows would let a single
 * frustrated user outrank a question a dozen businesses asked once. Ties fall
 * back to total occurrences, then to recency, so the order is stable between
 * loads.
 *
 * Each group carries the most recent spelling of the question, since that is
 * the one a reader is most likely to recognise.
 */
export function groupQuestions(rows: AssistantTurnRow[]): QuestionGroup[] {
  const byKey = new Map<
    string,
    {
      key: string;
      question: string;
      businessIds: Set<string>;
      occurrences: number;
      lastAsked: string;
      failureReasons: Set<string>;
    }
  >();

  for (const row of rows) {
    const key = normalizeQuestion(row.question);
    if (key === "") continue;

    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        key,
        question: row.question,
        businessIds: new Set([row.businessId]),
        occurrences: 1,
        lastAsked: row.created,
        failureReasons: new Set(reasonsOf(row)),
      });
      continue;
    }

    existing.businessIds.add(row.businessId);
    existing.occurrences += 1;
    for (const reason of reasonsOf(row)) existing.failureReasons.add(reason);
    if (row.created > existing.lastAsked) {
      existing.lastAsked = row.created;
      existing.question = row.question;
    }
  }

  return [...byKey.values()]
    .map((group): QuestionGroup => ({
      key: group.key,
      question: group.question,
      merchants: group.businessIds.size,
      occurrences: group.occurrences,
      lastAsked: group.lastAsked,
      failureReasons: [...group.failureReasons].sort(),
    }))
    .sort(
      (a, b) =>
        b.merchants - a.merchants ||
        b.occurrences - a.occurrences ||
        b.lastAsked.localeCompare(a.lastAsked) ||
        a.key.localeCompare(b.key),
    );
}
